// ============================================
// 模型轴心修正（2026-10-07）：第三方模型（FBX/GLB）常见“视觉中心 ≠ 原点”
// —— 拖动/旋转手柄挂在模型原点，导致手柄飘在模型外。修正 = 平移内部内容使
//    原点落到包围盒锚点（贴地中心 / 几何中心），并补偿模型 position 保证视觉不动。
// ============================================

export interface PivotBox {
  min: [number, number, number];
  max: [number, number, number];
}

export type PivotMode = 'ground' | 'center';

/** 锚点（content-local）：ground=底面中心（贴地），center=几何中心 */
export function pivotAnchor(box: PivotBox, mode: PivotMode): [number, number, number] {
  const cx = (box.min[0] + box.max[0]) / 2;
  const cz = (box.min[2] + box.max[2]) / 2;
  const cy = mode === 'ground' ? box.min[1] : (box.min[1] + box.max[1]) / 2;
  return [cx, cy, cz];
}

export const round3 = (v: number): number => {
  const r = Math.round(v * 1000) / 1000;
  return r === 0 ? 0 : r; // 归一化 -0
};

export interface Trs {
  /** 四元数 [x,y,z,w] */
  quat: [number, number, number, number];
  /** 缩放 [x,y,z] */
  scale: [number, number, number];
}

/** 纯函数：局部向量 v 经 TRS 旋转+缩放后的位移（等价 THREE: v.applyQuaternion(q).multiply(s)） */
export function transformVector(v: [number, number, number], t: Trs): [number, number, number] {
  const [x, y, z] = v;
  const [qx, qy, qz, qw] = t.quat;
  // 四元数旋转公式（向量绕 q 旋转）
  const ix = qw * x + qy * z - qz * y;
  const iy = qw * y + qz * x - qx * z;
  const iz = qw * z + qx * y - qy * x;
  const iw = -qx * x - qy * y - qz * z;
  const rx = ix * qw + iw * -qx + iy * -qz - iz * -qy;
  const ry = iy * qw + iw * -qy + iz * -qx - ix * -qz;
  const rz = iz * qw + iw * -qz + ix * -qy - iy * -qx;
  return [rx * t.scale[0], ry * t.scale[1], rz * t.scale[2]];
}

/**
 * 自动轴心修正的位移计算：
 * - contentShift：内容应平移量（content.position -= contentShift 后，原点落到锚点）
 * - groupShift：组位置补偿量（group.position += groupShift，保持视觉不动）
 */
export function autoPivotShift(
  anchor: [number, number, number],
  content: Trs,
  group: Trs,
): { contentShift: [number, number, number]; groupShift: [number, number, number] } {
  const contentShift = transformVector(anchor, content);
  const groupShift = transformVector(contentShift, group);
  return { contentShift, groupShift };
}

export function isZeroOffset(v?: [number, number, number] | null): boolean {
  return !v || (Math.abs(v[0]) < 1e-6 && Math.abs(v[1]) < 1e-6 && Math.abs(v[2]) < 1e-6);
}
